/**
 * IdeaCard Component
 *
 * Unified card across three surfaces:
 * - `viewMode="admin"` (default): full rich card with status badge,
 *   copy-id, linked-resources, footer actions.
 * - `viewMode="creator"`: same layout, defaults to
 *   `hideInternalResources` + `allowEditAllStatuses` + `clickToEdit`.
 *   Used by the photographer brief view.
 * - `viewMode="client"`: minimised layout for the client portal review
 *   surface. Title + concept clamp + "tap to see X" hint + 5-star
 *   rating footer. No status badge, no copy-id, no admin actions.
 *
 * Precedent: MediaCard's viewMode prop.
 */

import React, { useState } from "react";
import { IdeaConceptDisplay } from "./IdeaConceptDisplay";
import { IdeaStarRating } from "./IdeaStarRating";
import { LinkedResources } from "@/components/ui/LinkedResources";
import { ResourcePill } from "@/components/ui/ResourcePill";
import {
  CopyIcon,
  CheckIcon,
  StarIcon,
  PlayIcon,
  LinkIcon,
  ChevronRightIcon,
} from "@/components/icons";
import { copyToClipboard } from "@/utils/clipboard";
import { Idea, IdeaStatus, IdeaContentType } from "../types";
import { getIdeaConcept } from "../ideaHelpers";
import { IDEA_STATUS_CONFIG } from "../ideaStatusConfig";

export type IdeaCardViewMode = "admin" | "creator" | "client";

interface IdeaCardProps {
  idea: Idea;
  viewMode?: IdeaCardViewMode;
  onEdit?: (idea: Idea) => void;
  onDelete?: (idea: Idea) => void;
  onSubmit?: (idea: Idea) => void;
  onRevert?: (idea: Idea) => void;
  onAddToBrief?: (idea: Idea) => void;
  /**
   * When provided, an "Unlink" button appears for ideas currently
   * attached to a brief (idea.briefId set). Used by the admin brief
   * detail screens to remove an idea from the brief without deleting
   * the idea.
   */
  onRemoveFromBrief?: (idea: Idea) => void;
  /**
   * When true, suppress the internal-resource UI (attached posts,
   * linked collections / ideas / media, entity-link widgets). External
   * ``idea.resources`` URLs, status badge, title and concept stay
   * visible. Defaults to true in creator mode.
   */
  hideInternalResources?: boolean;
  /**
   * When true, the Edit affordance is shown regardless of idea status.
   * Used by the Creator view so the on-site photographer can enrich
   * APPROVED ideas. Defaults to true in creator mode.
   */
  allowEditAllStatuses?: boolean;
  /**
   * When true, clicking the card opens the edit drawer via ``onEdit``
   * and the footer Edit button is suppressed. Defaults to true in
   * creator mode.
   */
  clickToEdit?: boolean;

  // ----- client view props -----
  /** Current client rating (1-5) or null when unrated. */
  rating?: number | null;
  /** Called when the client picks a rating. Pass null to clear. */
  onRate?: (rating: number | null) => void;
  /** Called when the client taps the card to open the detail drawer. */
  onOpen?: () => void;
  /** Disable the rating control (e.g. while a write is in flight). */
  disabled?: boolean;
}

// Localised "open to see the X" hint copy for the client view. Italian
// first per the rest of the portal; English is fallback.
const SOURCE_HINT: Record<IdeaContentType, { it: string; en: string }> = {
  REEL: { it: "Apri per vedere il Reel", en: "Tap to see the Reel" },
  CAROUSEL: { it: "Apri per vedere il Carousel", en: "Tap to see the Carousel" },
  POST: { it: "Apri per vedere il Post", en: "Tap to see the Post" },
  STORY: { it: "Apri per vedere la Story", en: "Tap to see the Story" },
};
const GENERIC_SOURCE_HINT = { it: "Apri il riferimento", en: "Open reference" };
const NO_SOURCE_HINT = { it: "Apri per vedere altri dettagli", en: "Tap for more detail" };

function clientHintFor(
  contentType: IdeaContentType | null | undefined,
  hasSources: boolean,
): string {
  if (!hasSources) return NO_SOURCE_HINT.it;
  const entry = contentType ? SOURCE_HINT[contentType] : GENERIC_SOURCE_HINT;
  return entry.it;
}

// Status colors come from the shared config; icons are specific to this card variant
const STATUS_ICONS: Record<IdeaStatus, React.ReactNode> = {
  DRAFT: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  ),
  PENDING: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  APPROVED: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  READY: <PlayIcon className="w-3.5 h-3.5" />,
  REJECTED: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  COMPLETE: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const CONTENT_TYPE_CONFIG: Record<IdeaContentType, { label: string; icon: React.ReactNode }> = {
  POST: {
    label: "Post",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  REEL: {
    label: "Reel",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  },
  CAROUSEL: {
    label: "Carousel",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  STORY: {
    label: "Story",
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
  },
};

export const IdeaCard: React.FC<IdeaCardProps> = ({
  idea,
  viewMode = "admin",
  onEdit,
  onDelete,
  onSubmit,
  onRevert,
  onAddToBrief,
  onRemoveFromBrief,
  hideInternalResources,
  allowEditAllStatuses,
  clickToEdit,
  rating = null,
  onRate,
  onOpen,
  disabled = false,
}) => {
  // Creator mode flips the three behavior flags on by default. Explicit
  // props still win so callers can override on a per-instance basis.
  const isCreator = viewMode === "creator";
  const effectiveHideInternalResources =
    hideInternalResources ?? isCreator;
  const effectiveAllowEditAllStatuses =
    allowEditAllStatuses ?? isCreator;
  const effectiveClickToEdit = clickToEdit ?? isCreator;

  // Hooks must run unconditionally on every render — keep them above the
  // viewMode branch. The client branch doesn't use copiedId but the cost
  // of carrying one boolean is negligible.
  const [copiedId, setCopiedId] = useState(false);

  // ----- Client view: minimised card for the client portal review -----
  if (viewMode === "client") {
    const hasSources = idea.resources && idea.resources.length > 0;
    const isRated = rating !== null;
    const concept = getIdeaConcept(idea);
    return (
      <article
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen?.();
          }
        }}
        role="button"
        tabIndex={0}
        className={`group relative bg-white rounded-2xl transition-all cursor-pointer ${
          isRated ? "hover:shadow-sm" : "hover:shadow"
        }`}
      >
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-lg font-bold tracking-tight text-gray-900 leading-tight">
            {idea.title}
          </h3>
          {concept && (
            <div className="mt-2 text-sm text-gray-600 leading-relaxed">
              <IdeaConceptDisplay
                concept={concept}
                variant="preview"
                maxLength={140}
                className="line-clamp-3"
              />
            </div>
          )}
        </div>

        <div className="px-5 pb-3">
          <div
            className={`inline-flex items-center gap-1.5 text-xs font-medium ${
              hasSources ? "text-blue-700" : "text-gray-500"
            }`}
          >
            {hasSources ? (
              idea.contentType === "REEL" || idea.contentType === "STORY" ? (
                <PlayIcon className="w-3.5 h-3.5" />
              ) : (
                <LinkIcon className="w-3.5 h-3.5" />
              )
            ) : (
              <ChevronRightIcon className="w-3.5 h-3.5" />
            )}
            {clientHintFor(idea.contentType, hasSources)}
          </div>
        </div>

        <div
          className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em]">
            {isRated ? "Il tuo voto" : "Vota"}
          </span>
          <IdeaStarRating
            value={rating}
            onChange={(v) => onRate?.(v)}
            disabled={disabled}
            size="md"
            ariaLabel={`Vota: ${idea.title}`}
          />
        </div>
      </article>
    );
  }

  // ----- Admin / Creator: full rich card -----
  const statusConfig = IDEA_STATUS_CONFIG[idea.status];
  const statusIcon = STATUS_ICONS[idea.status];
  const contentTypeConfig = idea.contentType ? CONTENT_TYPE_CONFIG[idea.contentType] : null;

  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(idea.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };
  const canEdit =
    effectiveAllowEditAllStatuses ||
    idea.status === "DRAFT" ||
    idea.status === "REJECTED" ||
    idea.status === "READY";
  const canSubmit = idea.status === "DRAFT";
  const canRevert = idea.status === "REJECTED";
  const canDelete = idea.status === "DRAFT";
  const canAddToBrief = idea.status === "APPROVED" && !idea.briefId;
  const canRemoveFromBrief = !!idea.briefId;

  const handleCardClick = () => {
    if (effectiveClickToEdit && onEdit) onEdit(idea);
  };

  return (
    <div
      role={effectiveClickToEdit ? "button" : undefined}
      tabIndex={effectiveClickToEdit ? 0 : undefined}
      onClick={effectiveClickToEdit ? handleCardClick : undefined}
      onKeyDown={
        effectiveClickToEdit
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
      className={`group bg-white rounded-xl hover:shadow-md transition-all duration-200 ${
        effectiveClickToEdit ? "cursor-pointer" : ""
      }`}
    >
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate leading-tight">
              {idea.title}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}
              >
                {statusIcon}
                {statusConfig.label}
              </span>
              {contentTypeConfig && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                  {contentTypeConfig.icon}
                  {contentTypeConfig.label}
                </span>
              )}
              <button
                onClick={handleCopyId}
                className={`p-1 rounded-md transition-colors ${
                  copiedId
                    ? "bg-green-100 text-green-600"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                }`}
                title={copiedId ? "Copied!" : `Copy ID: ${idea.id}`}
              >
                {copiedId ? (
                  <CheckIcon className="w-3.5 h-3.5" />
                ) : (
                  <CopyIcon className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          </div>
          {idea.briefId && (
            <div className="shrink-0">
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-100">
                In Brief
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 pb-3">
        <IdeaConceptDisplay concept={getIdeaConcept(idea)} variant="preview" maxLength={120} className="text-sm text-gray-600 line-clamp-2 leading-relaxed" />

        {idea.resources.length > 0 && (
          <div
            className="mt-3 flex flex-wrap items-center gap-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            {idea.resources.map((r, i) => (
              <ResourcePill key={`${r.url}-${i}`} url={r.url} note={r.note} />
            ))}
          </div>
        )}

        {!effectiveHideInternalResources && (
          <LinkedResources entityType="idea" entityId={idea.id} className="mt-3" />
        )}

        {idea.clientFeedback && idea.status === "REJECTED" && (
          <div className="mt-3 p-3 bg-rose-50 border border-rose-100 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              <p className="text-sm text-rose-700">{idea.clientFeedback}</p>
            </div>
          </div>
        )}

        {/* Client review surface — stars and optional comment that the
            client left in the portal. Mirrors the pattern used on the
            media preview modal where the admin sees what the client
            said. Only renders when at least one signal is present. */}
        {(typeof idea.clientRating === "number" || idea.clientComment) && (
          <div className="mt-3 p-3 bg-amber-50/60 border border-amber-100 rounded-lg">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-semibold text-amber-700 uppercase tracking-[0.08em]">
                Client review
              </span>
              {typeof idea.clientRating === "number" && (
                <span className="inline-flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      className={`w-3.5 h-3.5 ${
                        star <= (idea.clientRating ?? 0)
                          ? "text-amber-500"
                          : "text-amber-200"
                      }`}
                      filled={star <= (idea.clientRating ?? 0)}
                    />
                  ))}
                </span>
              )}
            </div>
            {idea.clientComment && (
              <p className="text-sm text-amber-900 leading-relaxed">
                “{idea.clientComment}”
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-50 bg-gray-50/50 rounded-b-xl">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {new Date(idea.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
          <div className="flex items-center gap-1">
            {canAddToBrief && onAddToBrief && (
              <button
                onClick={(e) => { e.stopPropagation(); onAddToBrief(idea); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add to Brief
              </button>
            )}
            {canSubmit && onSubmit && (
              <button
                onClick={(e) => { e.stopPropagation(); onSubmit(idea); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Submit
              </button>
            )}
            {canRevert && onRevert && (
              <button
                onClick={(e) => { e.stopPropagation(); onRevert(idea); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                Revise
              </button>
            )}
            {canEdit && onEdit && !effectiveClickToEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(idea); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
            )}
            {canRemoveFromBrief && onRemoveFromBrief && (
              <button
                onClick={(e) => { e.stopPropagation(); onRemoveFromBrief(idea); }}
                title="Remove from brief"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Unlink
              </button>
            )}
            {canDelete && onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(idea); }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-md transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
